import { type FC, useCallback } from 'react'

import { getMimeTypeExtensionMap } from '@common/utils/file'
import { useUnit } from 'effector-react'
import Dropzone from 'react-dropzone'

import TrashIcon from '@/assets/icons/icon-trash.svg'
import UploadIcon from '@/assets/icons/icon-upload.svg'
import { Button } from '@/components/ui/button.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import {
  $filesToAdd,
  addFiles,
  removeFile,
  uploadAllMediaFx,
} from '@/dialogs/AddMediaDialog/models.ts'
import { isImage, isVideo } from '@/utils/file.ts'
import { classNames } from '@/utils/helpers.ts'

function getFileNameParts(file: File) {
  const extension = file.name.substring(file.name.lastIndexOf('.') + 1)
  const fileName = file.name.substring(0, file.name.lastIndexOf('.'))

  return [fileName, extension]
}

const acceptedFileTypes = getMimeTypeExtensionMap({
  types: ['image', 'video', 'audio', 'document'],
  subTypes: { video: ['mp4', 'mov'], document: ['pdf'] },
})

interface Props {}

export const VaultFileSelector: FC<Props> = () => {
  const loading = useUnit(uploadAllMediaFx.pending)
  const files = useUnit($filesToAdd)

  const toast = useToast()

  const handleDropRejected = useCallback(() => {
    toast.error(
      "This file type isn't supported. Please upload JPG, PNG, GIF (images), MP4, MOV (videos), MP3 (audio), or PDF (documents).",
    )
  }, [toast])

  return (
    <div>
      <Dropzone
        accept={acceptedFileTypes}
        multiple
        disabled={loading}
        onDrop={addFiles}
        onDropRejected={handleDropRejected}
      >
        {({ getInputProps, getRootProps, isDragReject }) => (
          <div
            {...getRootProps()}
            className="bg-secondary-50 dark:bg-secondary-600 flex flex-col items-center justify-center rounded border border-dashed p-6"
          >
            <input {...getInputProps()} />
            <UploadIcon className="h-5 w-5" />
            <p className="mt-4 font-medium">
              Drag & drop your files here or{' '}
              <span
                className={classNames(
                  'text-primary-500 cursor-pointer underline',
                  isDragReject && 'text-red-500',
                )}
              >
                choose file
              </span>
            </p>
          </div>
        )}
      </Dropzone>

      {!!files.length && (
        <div className="mt-5">
          {files.map(({ file: f, isUploading, uploadingProgress }, i) => {
            const isImageFile = isImage(f.name)
            const isVideoFile = isVideo(f.name)
            const [name, extension] = getFileNameParts(f)
            const url = isImageFile ? URL.createObjectURL(f) : ''

            return (
              <div
                key={f.name}
                className="hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900 mb-2 flex items-center rounded border px-3 py-2 duration-200 last:mb-0"
              >
                <div className="h-12 w-12 rounded-sm">
                  {isImageFile ? (
                    <img
                      src={url}
                      className="h-full w-full rounded-sm object-cover"
                      alt=""
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center rounded-sm border text-lg font-medium">
                      {isVideoFile ? 'V' : 'A'}
                    </div>
                  )}
                </div>
                <div className="mx-2 flex-1 overflow-hidden">
                  <p className="text-text-default text-ellipsis overflow-hidden">
                    {name}
                  </p>
                  <p className="text-secondary-300 text-xs font-medium">
                    {extension}
                  </p>
                </div>
                {isUploading && (
                  <div className="flex items-center">
                    <p className="text-md text-text-default font-medium">
                      {uploadingProgress}%
                    </p>
                  </div>
                )}
                <Button
                  disabled={loading}
                  onClick={() => removeFile(i)}
                  variant="ghost"
                >
                  <TrashIcon className="text-danger-default" />
                </Button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
